import { httpClient } from './core';
import { grundHeader } from '../../components/ui/loeschen-mit-grund-dialog';

// Email Templates
export interface EmailTemplateDto {
  id: number;
  template_key: string;
  subject: string;
  body_html: string;
  body_text?: string | null;
  description?: string | null;
  is_active: number | boolean;
  created_at: string;
  updated_at: string;
  /** Gesetzt bei einer Standort-Vorlage; ohne gilt sie global (T-0179). */
  standort_id?: number | null;
}

/** Ohne Standort: die globalen Vorlagen; mit: genau die des Standorts (T-0179). */
const standortQuery = (standortId?: number | null) =>
  standortId ? `?standort_id=${encodeURIComponent(String(standortId))}` : '';

export const emailTemplatesApi = {
  list: async (standortId?: number | null): Promise<EmailTemplateDto[]> => {
    const res = await httpClient<EmailTemplateDto[]>(`/api/email-templates${standortQuery(standortId)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der E-Mail-Vorlagen');
    return res.data;
  },
  get: async (key: string): Promise<EmailTemplateDto> => {
    const res = await httpClient<EmailTemplateDto>(`/api/email-templates/${encodeURIComponent(key)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der E-Mail-Vorlage');
    return res.data;
  },
  create: async (payload: { template_key: string; subject: string; body_html: string; body_text?: string | null; description?: string | null; is_active?: boolean; standort_id?: number | null }): Promise<EmailTemplateDto> => {
    const res = await httpClient<EmailTemplateDto>('/api/email-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Anlegen der E-Mail-Vorlage');
    return res.data;
  },
  update: async (key: string, payload: Partial<{ subject: string; body_html: string; body_text: string | null; description: string | null; is_active: boolean }>, standortId?: number | null): Promise<EmailTemplateDto> => {
    const res = await httpClient<EmailTemplateDto>(`/api/email-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Aktualisieren der E-Mail-Vorlage');
    return res.data;
  },
  delete: async (key: string, grund: string, standortId?: number | null): Promise<void> => {
    const res = await httpClient(`/api/email-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Fehler beim Löschen der E-Mail-Vorlage');
  },
};

export interface EmailTemplatePreviewResponse {
  template_key: string;
  standort_id: number;
  trial_request_id: number;
  subject: string;
  html: string;
  text?: string | null;
  from_address?: string;
  from_name?: string;
}

/** Datensatz für die Vorschau (T-0180); die Art folgt aus dem Vorlagenschlüssel. */
export type VorschauDatensatz = { record_type: string; record_id: number };

export interface VorschauDatensaetze {
  art: string | null;
  label: string | null;
  items: { id: number; label: string; detail: string }[];
}

export const templatePreviewApi = {
  records: async (templateKey: string, q: string, standortId?: number | null): Promise<VorschauDatensaetze> => {
    const params = new URLSearchParams({ template_key: templateKey });
    if (q.trim()) params.set('q', q.trim());
    if (standortId) params.set('standort_id', String(standortId));
    const res = await httpClient<VorschauDatensaetze>(`/api/template-preview/records?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Datensätze konnten nicht geladen werden');
    return res.data;
  },
};

export const smsTemplatesPreviewApi = {
  render: async (
    payload: { template_key: string; body: string; standort_id?: number | null } & Partial<VorschauDatensatz>,
  ): Promise<{ text: string }> => {
    const res = await httpClient<{ text: string }>('/api/sms-templates/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Rendern der Vorschau');
    return res.data;
  },
};

export const emailTemplatesPreviewApi = {
  render: async (
    payload: { template_key: string; standort_id: number; trial_request_id?: number } & Partial<VorschauDatensatz>,
  ): Promise<EmailTemplatePreviewResponse> => {
    const res = await httpClient<EmailTemplatePreviewResponse>('/api/email-templates/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Rendern der Vorschau');
    return res.data;
  },
};

// SMS Templates
export interface SmsTemplateDto {
  id: number;
  template_key: string;
  body: string;
  description?: string | null;
  is_active: number | boolean;
  created_at: string;
  updated_at: string;
  standort_id?: number | null;
}

export const smsTemplatesApi = {
  list: async (standortId?: number | null): Promise<SmsTemplateDto[]> => {
    const res = await httpClient<SmsTemplateDto[]>(`/api/sms-templates${standortQuery(standortId)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der SMS-Vorlagen');
    return res.data;
  },
  get: async (key: string): Promise<SmsTemplateDto> => {
    const res = await httpClient<SmsTemplateDto>(`/api/sms-templates/${encodeURIComponent(key)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der SMS-Vorlage');
    return res.data;
  },
  create: async (payload: { template_key: string; body: string; description?: string | null; is_active?: boolean; standort_id?: number | null }): Promise<SmsTemplateDto> => {
    const res = await httpClient<SmsTemplateDto>('/api/sms-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Anlegen der SMS-Vorlage');
    return res.data;
  },
  update: async (key: string, payload: Partial<{ body: string; description: string | null; is_active: boolean }>, standortId?: number | null): Promise<SmsTemplateDto> => {
    const res = await httpClient<SmsTemplateDto>(`/api/sms-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Aktualisieren der SMS-Vorlage');
    return res.data;
  },
  delete: async (key: string, grund: string, standortId?: number | null): Promise<void> => {
    const res = await httpClient(`/api/sms-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Fehler beim Löschen der SMS-Vorlage');
  },
};

// History Templates
export interface HistoryTemplateDto {
  id: number;
  template_key: string;
  category: string;
  history_body: string;
  description?: string | null;
  is_active: number | boolean;
  auto_create_task: number | boolean;
  task_body?: string | null;
  task_assignee_strategy?: string | null;
  task_due_offset_value?: number | null;
  task_due_offset_unit?: string | null;
  created_at: string;
  updated_at: string;
  standort_id?: number | null;
}

export interface RenderHistoryTemplateResponse {
  template_key: string;
  history: string;
  task?: {
    description: string;
    assignedUserId: number | null;
    dueAt: string;
    assigneeStrategy?: string;
    dueOffsetValue?: number | null;
    dueOffsetUnit?: string | null;
  } | null;
}

export const historyTemplatesApi = {
  list: async (category?: string, standortId?: number | null): Promise<HistoryTemplateDto[]> => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (standortId) params.set('standort_id', String(standortId));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await httpClient<HistoryTemplateDto[]>(`/api/history-templates${query}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Historien-Vorlagen');
    return res.data;
  },
  get: async (key: string): Promise<HistoryTemplateDto> => {
    const res = await httpClient<HistoryTemplateDto>(`/api/history-templates/${encodeURIComponent(key)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Historien-Vorlage');
    return res.data;
  },
  create: async (
    payload: {
      template_key: string;
      category?: string;
      history_body: string;
      description?: string | null;
      is_active?: boolean;
      auto_create_task?: boolean;
      task_body?: string | null;
      task_assignee_strategy?: string | null;
      task_due_offset_value?: number | null;
      task_due_offset_unit?: string | null;
      standort_id?: number | null;
    }
  ): Promise<HistoryTemplateDto> => {
    const res = await httpClient<HistoryTemplateDto>('/api/history-templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Anlegen der Historien-Vorlage');
    return res.data;
  },
  update: async (
    key: string,
    payload: Partial<{
      category: string;
      history_body: string;
      description: string | null;
      is_active: boolean;
      auto_create_task: boolean;
      task_body: string | null;
      task_assignee_strategy: string | null;
      task_due_offset_value: number | null;
      task_due_offset_unit: string | null;
    }>,
    standortId?: number | null
  ): Promise<HistoryTemplateDto> => {
    const res = await httpClient<HistoryTemplateDto>(`/api/history-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Aktualisieren der Historien-Vorlage');
    return res.data;
  },
  delete: async (key: string, grund: string, standortId?: number | null): Promise<void> => {
    const res = await httpClient(`/api/history-templates/${encodeURIComponent(key)}${standortQuery(standortId)}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Fehler beim Deaktivieren der Historien-Vorlage');
  },
  /** Mit Standort gilt dessen Vorlage vor der globalen — wie beim Anlegen des Eintrags. */
  render: async (payload: { template_key: string; vars?: Record<string, unknown>; actor_user_id?: number; standort_id?: number | null }): Promise<RenderHistoryTemplateResponse> => {
    const res = await httpClient<RenderHistoryTemplateResponse>('/api/history-templates/render', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Rendern der Historien-Vorlage');
    return res.data;
  },
};

// Template Variables
export interface TemplateVariablesItem {
  key: string;
  variables: string[];
}

export interface TemplateVariableMeta {
  key: string;
  label: string;
  example?: string;
}

export const templateVariablesApi = {
  list: async (): Promise<TemplateVariablesItem[]> => {
    const res = await httpClient<{ items: TemplateVariablesItem[] }>('/api/template-variables');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Template-Variablen');
    return res.data.items || [];
  },
  get: async (key: string): Promise<string[]> => {
    const res = await httpClient<{ key: string; variables: string[] }>(`/api/template-variables?key=${encodeURIComponent(key)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Template-Variablen');
    return res.data.variables || [];
  },
  getMeta: async (key: string): Promise<TemplateVariableMeta[]> => {
    const res = await httpClient<{ key: string; variables: string[]; variables_meta?: TemplateVariableMeta[] }>(
      `/api/template-variables?key=${encodeURIComponent(key)}&meta=1`
    );
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Template-Variablen');
    return res.data.variables_meta || [];
  },
};
